import useSWR, { mutate } from 'swr';
import '../ui/emergencyPopup.css';

// logic
import { useHistory } from 'react-router-dom';
import { createTicketFormSchema } from '../ticketForm/config/ticketFormSchema';
import ticketService from 'modules/emergencies/ticketService';
import useCancelTicketModal from '../modals/useCancelTicketModal';
import useMoveToTMSModal from '../modals/useMoveToTMSModal';
import { STATES } from 'modules/emergencies/constants';
import useConfirmTicketEditionBeforeAcceptance from '../modals/useConfirmTicketEditionBeforeAcceptance';
import usePriceNegotiationPermissions from 'modules/emergencies/permissions/usePriceNegotiationPermissions';
import PRICE_NEGOTIATION_PERMISSIONS_NAMES from 'modules/emergencies/permissions/priceNegotiationPermissionsNames';

const ticketDetailFetcher = async (...args) => {
  // url has this shape
  // ticketDetail/14/
  const id = args[0].replace('tickets/', '').replace('/', '');
  return ticketService.callBackend.getTicketDetail({ id });
};

// returns ticketDetail with loading/reloading state
const useTicketDetail = ({ id }) => {
  const canEditTicket = usePriceNegotiationPermissions(
    PRICE_NEGOTIATION_PERMISSIONS_NAMES.TICKET_EDITION
  );

  const { data: ticketDetail, error: ticketDetailError } = useSWR(
    `tickets/${id}/`,
    ticketDetailFetcher,
    {
      errorRetryCount: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const isLoadingTicketDetail = typeof ticketDetail === 'undefined' && !ticketDetailError;

  const history = useHistory();

  let initialValues = ticketService.defaultValues.getDefaultInitialFormValues();
  if (!isLoadingTicketDetail && !ticketDetailError)
    initialValues = ticketService.dataTransformation.getInitialFormValuesFromTicketDto({
      ticketDto: ticketDetail,
    });

  const validationSchema = createTicketFormSchema({ isEdit: true });

  const isEdit = ticketService.bussiness.canTicketBeEdited(ticketDetail);
  const isCancellable = ticketService.bussiness.canTicketBeCancelled(ticketDetail);
  const isReadOnly = !isEdit || !canEditTicket;
  const isDraft = ticketDetail?.synthetic_state === STATES.DRAFT;
  const draftErrors = ticketDetail?.draft_error_dict || {};
  const isQuoteAccepted = ticketDetail?.synthetic_state === STATES.ACCEPTED;
  const isDone = ticketDetail?.synthetic_state === STATES.DONE;

  const { openModalCancelTicket } = useCancelTicketModal(ticketDetail);
  const { openMoveToTMSModal } = useMoveToTMSModal(ticketDetail);

  const { openConfirmTicketEditionBeforeAcceptanceModal } =
    useConfirmTicketEditionBeforeAcceptance(ticketDetail);

  const onSubmit = async ({ ticketDto }) => {
    const updateTicketDetailDto = ticketService.dataTransformation.getUpdateTicketDetailDto({
      originalTicketDto: ticketDetail,
      newTicketDto: ticketDto,
    });
    if (ticketDetail?.state !== 'DRAFT') delete updateTicketDetailDto.shipper_id;
    const changes = ticketService.bussiness.getTicketChanges(
      { ...ticketDetail },
      { ...updateTicketDetailDto }
    );

    const result = await openConfirmTicketEditionBeforeAcceptanceModal(changes);
    if (result === undefined) {
      return 'canceled-saving-from-confirm-ticket-edition-before-acceptance';
    }
    await ticketService.callBackend.updateTicketDetail({ id, updateTicketDetailDto });
    // we are mutating the tickets cache so it will reload automatically
    mutate((key) => {
      return typeof key === 'string' && key.startsWith('tickets');
    });
    ticketService.utils.navigateToList({ history });
  };

  const onCancelTicket = async () => {
    openModalCancelTicket();
  };
  const onMarkTicketAsDone = async () => {
    openMoveToTMSModal();
  };

  const onCancel = async () => {
    ticketService.utils.navigateToList({ history });
  };

  return {
    ticketDetail,
    isLoadingTicketDetail,
    isQuoteAccepted,
    ticketDetailError,
    validationSchema,
    initialValues,
    isReadOnly,
    isEdit,
    isCancellable,
    isDone,
    isDraft,
    draftErrors,
    onSubmit,
    onCancel,
    onCancelTicket,
    onMarkTicketAsDone,
    mutate,
  };
};

export default useTicketDetail;
